import styled from "styled-components";
import css from "@styled-system/css";
import { layout, LayoutProps, position, PositionProps } from "styled-system";

import Box from "../Box/Box";
import Icon from "../Icon/Icon";
import Typography from "../Typography/Typography";

import { StepProps, LabelAlignment } from "./stepper.type";

const BasicStep = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  position: absolute;
  z-index: 1;
  top: 0;
`;

const Completed = styled(BasicStep)<{
  completedStateColor?: string;
}>`
  color: white;
  margin: 3px 0 6px;
  ${(props) =>
    css({
      backgroundColor: props.completedStateColor || "stepper-active-color",
    })}
`;

const Disabled = styled(BasicStep)`
  margin-bottom: 3px;
  border: 3px solid;
  ${css({
    backgroundColor: "primary-white",
    borderColor: "stepper-inactive-color",
  })}
`;

const Active = styled(BasicStep)<{
  activeStateColor?: string;
}>`
  border: 3px solid;
  margin-bottom: 3px;
  ${(props) =>
    css({
      backgroundColor: "primary-white",
      borderColor: props.activeStateColor || "stepper-active-color",
    })}
`;

const BulletWithStep = styled.div<{ labelAlignment: LabelAlignment }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) =>
    props.labelAlignment === "left" ? "flex-start" : "center"};
  flex: 1;
  position: relative;
`;

const BasicBar = styled.div<
  LayoutProps & PositionProps & { labelAlignment: LabelAlignment }
>`
  height: 4px;
  position: absolute;
  top: 14px;
  width: calc(100% - 32px);
  left: ${(props) =>
    props.labelAlignment === "left"
      ? "calc(-100% + 30px)"
      : "calc(-50% + 16px)"};
  ${css({
    borderRadius: 1,
  })}
  ${layout}
  ${position}
`;

const ActiveBar = styled(BasicBar)`
  width: ${(props) =>
    props.labelAlignment === "left"
      ? "calc(100% - 34px)"
      : "calc(100% - 35px)"};
  ${css({
    backgroundColor: "stepper-active-color",
  })}
`;

const BlueBar = styled(BasicBar)`
  ${css({
    backgroundColor: "stepper-active-color",
  })}
`;

const DisabledBar = styled(BasicBar)`
  width: ${(props) =>
    props.labelAlignment === "left"
      ? "calc(100% - 40px)"
      : "calc(100% - 38px)"};
  left: ${(props) =>
    props.labelAlignment === "left"
      ? "calc(-100% + 36px)"
      : "calc(-50% + 19px)"};
  ${css({
    backgroundColor: "stepper-inactive-color",
  })}
`;

const Label = styled(Box)<{
  status: string;
  labelAlignment: LabelAlignment;
}>`
  position: relative;
  top: 44px;
  margin: 0;
  opacity: ${(props) => (props.status === "disabled" ? "0.5" : "1")};
  text-align: ${(props) => props.labelAlignment};
  ${css({
    color: "lighter-text-color",
  })}
`;

const Step = ({
  index,
  activeStepIndex,
  step,
  labelAlignment,
  ...props
}: StepProps) => {
  const {
    label,
    subtitle,
    activeStateColor,
    activeStateIcon,
    completedStateColor,
    completedStateIcon,
  } = step;

  const status = getStatus(index, activeStepIndex);
  const steps = {
    completed: (
      <Completed completedStateColor={completedStateColor as string}>
        <Icon
          name={completedStateIcon?.name || "check"}
          weight={completedStateIcon?.weight || "solid"}
          color={completedStateIcon?.color || "primary-white"}
          width={completedStateIcon?.width || 14}
          height={completedStateIcon?.height || 14}
        ></Icon>
      </Completed>
    ),
    disabled: <Disabled />,
    active: (
      <Active activeStateColor={activeStateColor as string}>
        {activeStateIcon && (
          <Icon
            name={activeStateIcon?.name || "check"}
            weight={activeStateIcon?.weight || "solid"}
            color={
              (activeStateColor as string) ||
              activeStateIcon?.color ||
              "grey-blue"
            }
            width={activeStateIcon?.width || 16}
            height={activeStateIcon?.height || 16}
          ></Icon>
        )}
      </Active>
    ),
  };

  const bars = {
    completed: <BlueBar labelAlignment={labelAlignment} />,
    disabled: <DisabledBar labelAlignment={labelAlignment} />,
    active: <ActiveBar labelAlignment={labelAlignment} />,
  };

  return (
    <BulletWithStep {...props} labelAlignment={labelAlignment}>
      {index !== 0 && bars[status]}
      {steps[status]}
      <Label status={status} labelAlignment={labelAlignment}>
        <Typography
          variant="h6"
          m={0}
          color="lighter-text-color"
          textAlign={labelAlignment}
        >
          <span>{label}</span>
        </Typography>
        {subtitle && (
          <Typography
            variant="body-small"
            m={0}
            mt={2}
            textAlign={labelAlignment}
          >
            {subtitle}
          </Typography>
        )}
      </Label>
    </BulletWithStep>
  );
};

const getStatus = (index: number, currentStepIndex: number) => {
  if (index < currentStepIndex) {
    return "completed";
  }

  if (index > currentStepIndex) {
    return "disabled";
  }

  return "active";
};

export default Step;
